/**
 * Client helpers for the settlement_documents API (executor uploads per phase/step).
 */

import { apiService } from './api';

export interface SettlementDocumentRow {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

export function toDocumentUploadRow(d: SettlementDocumentRow) {
  return {
    id: d.id,
    name: d.filename,
    type: d.mimeType,
    size: d.size,
    uploadedAt: d.createdAt,
  };
}

export async function listSettlementDocuments(
  estateId: string,
  phase: string,
  stepId: string
): Promise<SettlementDocumentRow[]> {
  const query = `?phase=${encodeURIComponent(phase)}&stepId=${encodeURIComponent(stepId)}`;
  const res = await apiService.request(`/api/settlement/${estateId}/documents${query}`, {
    method: 'GET',
  });
  if (!res.success || !Array.isArray(res.documents)) return [];
  return res.documents.map((row: Record<string, unknown>) => ({
    id: String(row.id),
    filename: String(row.filename ?? ''),
    mimeType: String(row.mimeType ?? 'application/octet-stream'),
    size: Number(row.size ?? 0),
    createdAt: String(row.createdAt ?? new Date().toISOString()),
  }));
}

function readFileAsBase64Payload(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export async function uploadSettlementDocument(params: {
  estateId: string;
  phase: string;
  stepId: string;
  file: File;
  description?: string;
}): Promise<SettlementDocumentRow> {
  const data = await readFileAsBase64Payload(params.file);
  const res = await apiService.request(`/api/settlement/${params.estateId}/documents`, {
    method: 'POST',
    body: JSON.stringify({
      filename: params.file.name,
      mimeType: params.file.type || 'application/octet-stream',
      data,
      phase: params.phase,
      stepId: params.stepId,
      description: params.description ?? null,
    }),
  });
  const doc = res.document;
  return {
    id: String(doc.id),
    filename: String(doc.filename),
    mimeType: String(doc.mimeType),
    size: Number(doc.size),
    createdAt: String(doc.createdAt),
  };
}

export async function downloadSettlementDocument(
  estateId: string,
  documentId: string,
  filename: string
): Promise<void> {
  const res = await apiService.request(`/api/settlement/${estateId}/documents/${documentId}`, {
    method: 'GET',
  });
  const doc = res.document;
  const byteChars = atob(String(doc.data ?? ''));
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
  const blob = new Blob([bytes], { type: String(doc.mimeType ?? 'application/octet-stream') });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || String(doc.filename ?? 'document');
  a.click();
  URL.revokeObjectURL(url);
}

export async function deleteSettlementDocument(estateId: string, documentId: string): Promise<void> {
  await apiService.request(`/api/settlement/${estateId}/documents/${documentId}`, {
    method: 'DELETE',
  });
}
